import { API_BASE_URL, API_KEY } from "../utils/const";
import type { SubscriptionResponse } from "../interfaces/subscription";
import type {
  ApiEnvelope,
  CreateSubscriptionRequest,
  CreateSubscriptionResponse,
  DeleteSubscriptionResponse,
  Item,
  NotificationMethod,
  PositionTestResponse,
  Route,
  Topic,
} from "../types/api";
import type { PurgeUserRequest, PurgeUserResponse } from "../types/purge";

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export type ApiRequestOptions = {
  method?: HttpMethod;
  headers?: HeadersInit;
  body?: unknown;
  signal?: AbortSignal;
  baseUrl?: string;
  useApiKey?: boolean;
  token?: string;
};

const isAbsoluteUrl = (path: string) => /^https?:\/\//i.test(path);

const buildUrl = (baseUrl: string | undefined, path: string) => {
  if (!baseUrl || isAbsoluteUrl(path)) {
    return path;
  }
  const normalizedBase = baseUrl.endsWith("/")
    ? baseUrl.slice(0, -1)
    : baseUrl;
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${normalizedBase}${normalizedPath}`;
};

const buildHeaders = (options: ApiRequestOptions, hasBody: boolean) => {
  const headers = new Headers(options.headers);
  if (hasBody && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (options.useApiKey && API_KEY && !headers.has("x-api-key")) {
    headers.set("x-api-key", API_KEY);
  }
  if (options.token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${options.token}`);
  }
  return headers;
};

async function request<T>(
  path: string,
  options: ApiRequestOptions = {}
): Promise<T> {
  const method = options.method ?? "GET";
  const hasBody = options.body !== undefined;
  const url = buildUrl(options.baseUrl ?? API_BASE_URL, path);
  const headers = buildHeaders(options, hasBody);
  const body = hasBody ? JSON.stringify(options.body) : undefined;

  const res = await fetch(url, {
    method,
    headers,
    body,
    signal: options.signal,
  });

  const text = await res.text();
  let data: unknown = text;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  } else {
    data = null;
  }

  if (!res.ok) {
    const errorMessage =
      (data as { error?: string; message?: string })?.error ||
      (data as { error?: string; message?: string })?.message ||
      `HTTP ${res.status}`;
    throw new Error(errorMessage);
  }

  return data as T;
}

export const api = {
  routes: {
    async getAll(): Promise<Route[]> {
      const res = await request<ApiEnvelope<Route[]>>("/routes", {
        useApiKey: true,
      });
      return res.body;
    },
  },
  topics: {
    async getAll(): Promise<Topic[]> {
      const res = await request<ApiEnvelope<Topic[]>>("/topics", {
        useApiKey: true,
      });
      return res.body;
    },
  },
  notifications: {
    async getAll(): Promise<NotificationMethod[]> {
      const res = await request<ApiEnvelope<NotificationMethod[]>>(
        "/notifications",
        {
          useApiKey: true,
        }
      );
      return res.body;
    },
    positionTest(message: string): Promise<PositionTestResponse> {
      return request<PositionTestResponse>("/position-test", {
        method: "POST",
        body: { message },
      });
    },
  },
  subscriptions: {
    getByUser(userId: string): Promise<SubscriptionResponse[]> {
      return request<SubscriptionResponse[]>(
        `/subscriptions/users/${userId}`,
        {
          useApiKey: true,
        }
      );
    },
    deleteById(id: string): Promise<DeleteSubscriptionResponse> {
      return request<DeleteSubscriptionResponse>(`/subscriptions/${id}`, {
        method: "DELETE",
        useApiKey: true,
      });
    },
    create(payload: CreateSubscriptionRequest): Promise<CreateSubscriptionResponse> {
      return request<CreateSubscriptionResponse>("/subscriptions", {
        method: "POST",
        body: payload,
      });
    },
  },
  users: {
    purge(
      payload: PurgeUserRequest,
      opts?: { signal?: AbortSignal; token?: string }
    ): Promise<PurgeUserResponse> {
      return request<PurgeUserResponse>("/users/purge", {
        method: "POST",
        body: payload,
        useApiKey: true,
        signal: opts?.signal,
        token: opts?.token,
      });
    },
  },
  items: {
    getAll(): Promise<Item[]> {
      return request<Item[]>("/api/items", { baseUrl: "" });
    },
    getById(id: string): Promise<Item> {
      return request<Item>(`/api/items/${id}`, { baseUrl: "" });
    },
  },
};
